import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, Container, Alert, Spinner } from "react-bootstrap";
import { FaEnvelopeOpen } from "react-icons/fa";
import { FaPhoneFlip } from 'react-icons/fa6';
import apiCall from '../components/Api/api';
import ReCAPTCHA from "react-google-recaptcha";

const ContactUs = () => {
  const recaptchaRef = useRef(null);
  const timeoutRef = useRef(null);
  const secretKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    recaptchaToken: ''
  });

  const [errors, setErrors] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    recaptchaToken: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [responseMsg, setResponseMsg] = useState(null);

  useEffect(() => {
    // Cleanup 
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set timeout
    if (responseMsg) {
      timeoutRef.current = setTimeout(() => {
        setResponseMsg(null); // Clear 
      }, 2000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [responseMsg]);

  const validate = () => {
    let tempErrors = {};
    if (!formData.name) tempErrors.name = "Name is required";
    if (!formData.phone || !/^[0-9]+$/.test(formData.phone)) tempErrors.phone = "Valid phone number required";
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) tempErrors.email = "Valid email required";
    if (!formData.message) tempErrors.message = "Message cannot be empty";
    if (!formData?.recaptchaToken) tempErrors.recaptchaToken = "Please verify reCAPTCHA";
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    // Update form data
    setFormData(prev => ({ ...prev, [name]: value }));

    // If the field is related to errors, update the error state as well (e.g., clear the error message)
    if (errors.hasOwnProperty(name)) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);
    setResponseMsg(null);

    try {
      const formDataSubmit = new FormData();
      formDataSubmit.append('name', formData.name);
      formDataSubmit.append('phone', formData.phone);
      formDataSubmit.append('email', formData.email);
      formDataSubmit.append('message', formData.message);
      const response = await apiCall({
        url: 'contact-us',
        method: 'POST',
        data: formDataSubmit,
      });
      if (response.status !== 200) {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
      const apiData = response.data;
      if (apiData?.errors && Object.keys(apiData.errors).length > 0) {
        const errors = apiData.errors;
        // Dynamically set errors based on the response
        Object.keys(errors).forEach((key) => {
          const errorMessages = errors[key];
          const firstErrorMessage = errorMessages[0];
          // Set the error state for the corresponding field
          setErrors((prevData) => ({
            ...prevData,
            [key]: firstErrorMessage,
          }));
        });
        return;
      } else {
        setResponseMsg({ type: 'success', text: 'Thank you for contacting us!' });
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          recaptchaToken: ''
        });
        setErrors({
          name: '',
          phone: '',
          email: '',
          message: '',
          recaptchaToken: ''
        });
      }
    } catch (err) {
      setResponseMsg({ type: 'danger', text: err.message || "Something went wrong!" });
      setTimeout(() => {
        setResponseMsg(null); // Remove the message after 5 seconds
      }, 2000);
    } finally {
      setSubmitting(false);
    }
  };


  const handleRecaptchaChange = (token) => {
    setFormData(prev => ({ ...prev, recaptchaToken: token }));

    // Clear the recaptcha error if the token is successfully set
    setErrors(prev => ({ ...prev, recaptchaToken: '' }));
  };

  return (
    <section className="contact-wrappers mb-5">
      <Container fluid="lg" className="padding_bottom">
        <div className="contact-head">
          <h3 className="mb-3">Have A Query! Please Leave Your Contact Details.</h3>
        </div>

        <Row className="g-4">
          <Col xs={12}>
            <Row className='gy-4'>
              <Col md={6}>
                <div className="bg-light rounded p-xl-3">
                  <div className="d-flex align-items-center bg-white rounded p-3 icon-wrap">
                    <div className="icon me-3">
                      <FaEnvelopeOpen style={{ color: "#fff", fontSize: "20px" }} />
                    </div>
                    <span>
                      <a href="mailto:support@walkintothewild.in">support@walkintothewild.in</a>
                    </span>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="bg-light rounded p-xl-3">
                  <div className="d-flex align-items-center bg-white rounded p-3 icon-wrap">
                    <div className="icon me-3">
                      <FaPhoneFlip style={{ color: "#fff", fontSize: "20px" }} />
                    </div>
                    <span>
                      <a href="tel:+918882135724">+91 8882135724</a>
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
          </Col>

          <Col md={12} className='form-bg'>
            <Row>
              <div className="Up">
                <h5>Get In Touch</h5>

                {responseMsg && (
                  <Alert variant={responseMsg.type}>{responseMsg.text}</Alert>
                )}

                <Row className="g-3">
                  <Col md={6}>
                    <div className="mb-3">
                      <input
                        type="text"
                        name="name"
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        placeholder="Name*"
                        value={formData.name}
                        onChange={handleChange}
                      />
                      <div className="invalid-feedback">{errors.name}</div>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="mb-3">
                      <input
                        type="text"
                        name="phone"
                        className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                        placeholder="Phone*"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                      <div className="invalid-feedback">{errors.phone}</div>
                    </div>
                  </Col>

                  <Col xs={12}>
                    <div className="mb-3">
                      <input
                        type="email"
                        name="email"
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        placeholder="Email*"
                        value={formData.email}
                        onChange={handleChange}
                      />
                      <div className="invalid-feedback">{errors.email}</div>
                    </div>
                  </Col>

                  <Col xs={12}>
                    <div className="mb-3">
                      <textarea
                        name="message"
                        className={`form-control ${errors.message ? 'is-invalid' : ''}`}
                        placeholder="Message*"
                        rows={8}
                        value={formData.message}
                        onChange={handleChange}
                      ></textarea>
                      <div className="invalid-feedback">{errors.message}</div>
                    </div>
                  </Col>
                  <Col xs={12}>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={secretKey} // Replace with your actual site key
                      onChange={handleRecaptchaChange}
                    />
                    {errors?.recaptchaToken && (
                      <div className="text-danger fs-9">{errors.recaptchaToken}</div>
                    )}
                  </Col>



                  <Col xs={12} className='btn-bg'>
                    <button
                      type="button"
                      className="btn btn_newsafari organizeBtn newbg w-100 py-3"
                      onClick={handleSubmit}
                      disabled={submitting}
                    >
                      {submitting ? <Spinner animation="border" size="sm" /> : 'Submit'}
                    </button>
                  </Col>
                </Row>
              </div>
            </Row>
          </Col>

          <Col md={12} className='form-bg mt-5'>
            <Row>
              <div className="contact-head text-center">
                <h3 className="mb-3">MEDIARC TECHNOLOGIES PRIVATE LIMITED</h3>
              </div>
              <div className="contact-address mx-auto text-center">
                <p>
                  Block B-1 B1/639 A Spring House Coworking Janakpuri Near Metro Pillar No 563 Janakpuri, New Delhi - Pincode -110058
                </p>
              </div>
            </Row>
          </Col>

        </Row>
      </Container >
    </section >
  );
};

export default ContactUs;
